import React, { useEffect, useMemo } from 'react'
import { withStyles } from '@material-ui/core/styles'
import {
  Typography,
  Button,
  LinearProgress,
  Box,
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import AutoAwesomeIcon from '@material-ui/icons/AutoAwesome'
import RefreshIcon from '@material-ui/icons/Refresh'
import DescriptionIcon from '@material-ui/icons/Description'
import styles from './styles'

const AnalyzeStep = ({ classes, analyzeStream, onAnalyzeComplete }) => {
  const {
    analyzing,
    progress,
    step,
    result,
    partialResults,
    error,
    reconnecting,
    reconnectAttempt,
  } = analyzeStream

  useEffect(() => {
    if (result && !error) {
      onAnalyzeComplete(result)
    }
  }, [result, error, onAnalyzeComplete])

  const dedupedResults = useMemo(() => {
    if (!partialResults || partialResults.length === 0) return []
    const byName = new Map()
    for (const item of partialResults) {
      const existing = byName.get(item.file_name)
      if (!existing || item.status === 'done') {
        byName.set(item.file_name, item)
      }
    }
    return Array.from(byName.values())
  }, [partialResults])

  return (
    <div className={classes.root}>
      {(!result || analyzing) && !error && (
        <div className={classes.progressContainer}>
          <Box mb={2}>
            <AutoAwesomeIcon style={{ fontSize: '48px', color: '#3B82F6', marginBottom: '16px' }} />
            <Typography variant="h6" style={{ fontWeight: 700, color: '#0F172A' }}>
              {analyzing ? 'AI is analyzing your content...' : 'Preparing analysis...'}
            </Typography>
            <Typography style={{ color: '#64748B', fontSize: '14px', maxWidth: '400px', margin: '8px auto' }}>
              We're processing your PDFs to extract key concepts and generate a structured course outline.
            </Typography>
          </Box>

          {analyzing && (
            <Box mt={3} mb={1}>
              <LinearProgress variant="determinate" value={progress} className={classes.progressBar} />
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#3B82F6' }}>
                  {progress}% Complete
                </Typography>
                <Typography style={{ fontSize: '12px', color: '#64748B' }}>
                  {reconnecting ? `Reconnecting... (attempt ${reconnectAttempt}/3)` : step}
                </Typography>
              </Box>
              {reconnecting && (
                <Box mt={1}>
                  <Typography variant="caption" style={{ color: '#DC2626', fontWeight: 600 }}>
                    Connection lost. Trying to resume...
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {dedupedResults && dedupedResults.length > 0 && (
            <div className={classes.checklist}>
              {dedupedResults.map((item, idx) => (
                <div 
                  key={idx} 
                  className={`${classes.checklistItem} ${item.status === 'done' ? classes.checklistDone : ''}`}
                >
                  <DescriptionIcon style={{ color: item.status === 'done' ? '#3B82F6' : '#94A3B8', fontSize: '20px' }} />
                  <div className={classes.checklistText}>
                    <Typography style={{ fontSize: '13px', fontWeight: 500, textAlign: 'left' }}>
                      {item.file_name}
                    </Typography>
                    <Typography style={{ fontSize: '11px', color: '#64748B', textAlign: 'left' }}>
                      {item.page_range ? `Pages ${item.page_range.start}–${item.page_range.end}` : 'Processing...'}
                    </Typography>
                  </div>
                  {item.status === 'done' ? (
                    <CheckCircleIcon style={{ color: '#10B981', fontSize: '18px' }} />
                  ) : (
                    <RefreshIcon className={classes.spinner} style={{ fontSize: '18px' }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <Box p={4} textAlign="center" style={{ backgroundColor: '#FEF2F2', borderRadius: '16px', border: '1px solid #FECACA' }}>
          <Typography style={{ color: '#B91C1C', fontWeight: 600 }}>
            Analysis failed
          </Typography>
          <Typography style={{ color: '#EF4444', fontSize: '14px', marginTop: '8px' }}>
            {error}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            style={{ marginTop: '16px', textTransform: 'none', color: '#B91C1C', borderColor: '#FCA5A5' }}
          >
            Try Again
          </Button>
        </Box>
      )}

      {result && !analyzing && (
        <div className={classes.successCard}>
          <CheckCircleIcon style={{ fontSize: '56px', color: '#10B981', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ fontWeight: 700, color: '#064E3B' }}>
            Analysis Complete!
          </Typography>
          <Typography style={{ color: '#065F46', fontSize: '15px', marginTop: '8px' }}>
            Finalizing and starting generation...
          </Typography>
        </div>
      )}
    </div>
  )
}

export default withStyles(styles)(AnalyzeStep)
